import type {
  AbaDaFila,
  EstadoDoCaso,
  EtapaDaDivergencia,
  EtiquetaDoCaso,
  FiltrosDaFila,
  OrdemDaFila,
  ResultadoDaAuditoria,
} from '../../auditoria';
import {
  ROTULO_DA_ETAPA,
  ROTULO_DA_ETIQUETA,
  ROTULO_DO_ESTADO,
  ROTULO_DO_RESULTADO,
} from '../../auditoria';

/**
 * O CONTEXTO da fila viaja junto com o caso (dono, 05/10): período, filtros e ordem moram no
 * endereço da Central (#/divergencias?per=periodo&de=…) e o caso recebe esse endereço em `volta`.
 * Assim a fila lateral, o "próximo" e o "voltar" usam exatamente a mesma lista — nunca a fila
 * inteira — e voltar devolve a mesma posição da rolagem.
 */

/** 'tudo' = sem período: a fila de tarefas (etapas) mostra tudo o que está pendente. */
export type Periodo = 'tudo' | 'hoje' | 'ontem' | '7' | '30' | 'periodo';
export type FaixaDaDiferenca = '' | '1' | '2-3' | '4-5' | '6-10' | '10+';

export interface Filtros {
  readonly estados: readonly EstadoDoCaso[];
  readonly resultados: readonly ResultadoDaAuditoria[];
  readonly etiquetas: readonly EtiquetaDoCaso[];
  readonly setor: string | null;
  readonly pessoa: string | null;
  readonly busca: string | null;
  readonly periodo: Periodo;
  /** Só com período personalizado. AAAA-MM-DD. */
  readonly de: string | null;
  readonly ate: string | null;
  /** Incluir casos antigos ainda abertos (fora do período). */
  readonly abertos: boolean;
  readonly faixa: FaixaDaDiferenca;
  readonly ordem: OrdemDaFila;
  /** Divergências 3.0: a aba da etapa (só vale na aba 'fila'). null = a primeira (Prateleira). */
  readonly etapa: EtapaDaDivergencia | null;
  /** Aba "Anteriores": os casos abertos de antes das três etapas (no lugar da etapa). */
  readonly anteriores: boolean;
  /** Página do servidor (1, 2, …). */
  readonly pagina: number;
}

export const VAZIO: Filtros = {
  estados: [],
  resultados: [],
  etiquetas: [],
  setor: null,
  pessoa: null,
  busca: null,
  periodo: 'tudo',
  de: null,
  ate: null,
  abertos: false,
  faixa: '',
  ordem: 'PRIORIDADE',
  etapa: null,
  anteriores: false,
  pagina: 1,
};

/** As abas de etapa, na ordem do fluxo. */
export const ETAPAS_DA_FILA: readonly EtapaDaDivergencia[] = ['PRATELEIRA', 'ESTOQUE', 'AJUSTE'];
export const ETAPA_INICIAL: EtapaDaDivergencia = 'PRATELEIRA';
/** Casos por página nas abas de etapa (paginação no servidor); as outras abas seguem em 1000. */
export const POR_PAGINA = 50;

export const PERIODOS: readonly { periodo: Periodo; rotulo: string }[] = [
  { periodo: 'tudo', rotulo: 'Tudo' },
  { periodo: 'hoje', rotulo: 'Hoje' },
  { periodo: 'ontem', rotulo: 'Ontem' },
  { periodo: '7', rotulo: '7 dias' },
  { periodo: '30', rotulo: '30 dias' },
];

export const FAIXAS: readonly { faixa: FaixaDaDiferenca; rotulo: string }[] = [
  { faixa: '', rotulo: 'Todas' },
  { faixa: '1', rotulo: '1 un.' },
  { faixa: '2-3', rotulo: '2–3' },
  { faixa: '4-5', rotulo: '4–5' },
  { faixa: '6-10', rotulo: '6–10' },
  { faixa: '10+', rotulo: '10+' },
];
const LIMITES_DA_FAIXA: Record<Exclude<FaixaDaDiferenca, ''>, [number, number | null]> = {
  '1': [1, 1],
  '2-3': [2, 3],
  '4-5': [4, 5],
  '6-10': [6, 10],
  '10+': [11, null],
};

export const ORDENS: readonly { ordem: OrdemDaFila; rotulo: string }[] = [
  { ordem: 'PRIORIDADE', rotulo: 'Prioridade' },
  { ordem: 'DIFERENCA', rotulo: 'Maior diferença' },
  { ordem: 'ANTIGA', rotulo: 'Mais antiga' },
  { ordem: 'ROTA', rotulo: '🚶 Rota de reconferência' },
  { ordem: 'CATEGORIA', rotulo: 'Categoria' },
  { ordem: 'AZ', rotulo: 'Produto A–Z' },
];

/** Hoje em Brasília, AAAA-MM-DD. */
export function hojeBR(agora = new Date()): string {
  return agora.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}
export function maisDias(dia: string, n: number): string {
  const d = new Date(`${dia}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
const DIA = /^\d{4}-\d{2}-\d{2}$/;
export const diaBR = (dia: string) => `${dia.slice(8, 10)}/${dia.slice(5, 7)}/${dia.slice(0, 4)}`;

/** O intervalo de dias que o período escolhido representa. */
export function intervalo(f: Filtros, hoje = hojeBR()): { de: string; ate: string } {
  switch (f.periodo) {
    case 'ontem':
      return { de: maisDias(hoje, -1), ate: maisDias(hoje, -1) };
    case '7':
      return { de: maisDias(hoje, -6), ate: hoje };
    case '30':
      return { de: maisDias(hoje, -29), ate: hoje };
    case 'periodo': {
      const de = f.de && DIA.test(f.de) ? f.de : hoje;
      const ate = f.ate && DIA.test(f.ate) && f.ate >= de ? f.ate : de;
      return { de, ate };
    }
    default:
      return { de: hoje, ate: hoje };
  }
}

/** Filtros da tela → consulta da API (a mesma na Central e no caso). */
export function consultaDe(aba: AbaDaFila, f: Filtros): FiltrosDaFila {
  const { de, ate } = intervalo(f);
  const lim = f.faixa ? LIMITES_DA_FAIXA[f.faixa] : null;
  return {
    aba,
    estados: f.estados,
    resultados: f.resultados,
    etiquetas: f.etiquetas,
    setor: f.setor,
    pessoa: f.pessoa,
    busca: f.busca,
    // Recontagem pendente é tarefa da loja: não tem período. "Tudo" = sem período (concluídas:
    // o último ano).
    ...(aba === 'recontagens'
      ? {}
      : f.periodo === 'tudo'
        ? aba === 'fila'
          ? {}
          : { dias: 366 }
        : { de, ate, abertos: f.abertos }),
    difMin: lim ? lim[0] : null,
    difMax: lim ? lim[1] : null,
    ordem: f.ordem,
    // As abas de etapa paginam no servidor; a lateral do caso pede a MESMA página.
    ...(aba === 'fila'
      ? f.anteriores
        ? { fluxo: 'ANTERIOR' as const, pagina: f.pagina, limite: POR_PAGINA }
        : { etapa: f.etapa ?? ETAPA_INICIAL, pagina: f.pagina, limite: POR_PAGINA }
      : { limite: 1000 }),
  };
}

/* ---------- endereço ---------- */

export function paraParametros(f: Filtros): URLSearchParams {
  const p = new URLSearchParams();
  if (f.estados.length) p.set('estado', f.estados.join(','));
  if (f.resultados.length) p.set('resultado', f.resultados.join(','));
  if (f.etiquetas.length) p.set('etiqueta', f.etiquetas.join(','));
  if (f.setor) p.set('setor', f.setor);
  if (f.pessoa) p.set('pessoa', f.pessoa);
  if (f.busca?.trim()) p.set('busca', f.busca.trim());
  if (f.periodo !== 'tudo') p.set('per', f.periodo);
  if (f.periodo === 'periodo') {
    const { de, ate } = intervalo(f);
    p.set('de', de);
    p.set('ate', ate);
  }
  if (f.abertos) p.set('abertos', '1');
  if (f.faixa) p.set('dif', f.faixa);
  if (f.ordem !== 'PRIORIDADE') p.set('ordem', f.ordem);
  if (f.anteriores) p.set('anteriores', '1');
  else if (f.etapa) p.set('etapa', f.etapa);
  if (f.pagina > 1) p.set('pag', String(f.pagina));
  return p;
}

const lista = <T extends string>(v: string | null, validos: Readonly<Record<T, string>>): T[] =>
  (v ?? '').split(',').filter((x): x is T => x in validos);

export function deParametros(p: URLSearchParams): Filtros {
  const per = p.get('per');
  const dias = p.get('dias'); // endereço antigo (?dias=7) continua valendo
  const periodo: Periodo =
    per === 'hoje' || per === 'ontem' || per === '7' || per === '30' || per === 'periodo'
      ? per
      : dias === '7' || dias === '30'
        ? dias
        : 'tudo';
  const dif = p.get('dif') ?? '';
  const ordem = p.get('ordem');
  const etapa = p.get('etapa');
  const pag = Number(p.get('pag'));
  return {
    ...VAZIO,
    estados: lista(p.get('estado'), ROTULO_DO_ESTADO),
    resultados: lista(p.get('resultado'), ROTULO_DO_RESULTADO),
    etiquetas: lista(p.get('etiqueta'), ROTULO_DA_ETIQUETA),
    setor: p.get('setor'),
    pessoa: p.get('pessoa'),
    busca: p.get('busca'),
    periodo,
    de: p.get('de'),
    ate: p.get('ate'),
    abertos: p.get('abertos') === '1',
    faixa: FAIXAS.some((x) => x.faixa === dif) ? (dif as FaixaDaDiferenca) : '',
    ordem: ORDENS.some((o) => o.ordem === ordem) ? (ordem as OrdemDaFila) : 'PRIORIDADE',
    etapa: ETAPAS_DA_FILA.some((e) => e === etapa) ? (etapa as EtapaDaDivergencia) : null,
    anteriores: p.get('anteriores') === '1',
    pagina: Number.isInteger(pag) && pag > 1 ? pag : 1,
  };
}

const ROTA_DA_ABA: Record<AbaDaFila, string> = {
  fila: 'divergencias',
  recontagens: 'recontagens',
  concluidas: 'concluidas',
  historico: 'historico-auditoria',
};

/** "#/divergencias?per=7&setor=…" — o endereço da Central com estes filtros. */
export function enderecoDaCentral(aba: AbaDaFila, f: Filtros): string {
  // Etapa e página só existem na aba da fila; lá a etapa é sempre explícita no endereço, para a
  // lateral do caso e o "voltar" mostrarem exatamente a mesma lista.
  const q = paraParametros(
    aba === 'fila'
      ? f.anteriores
        ? { ...f, etapa: null }
        : { ...f, etapa: f.etapa ?? ETAPA_INICIAL }
      : { ...f, etapa: null, anteriores: false, pagina: 1 },
  ).toString();
  return `#/${ROTA_DA_ABA[aba]}${q ? `?${q}` : ''}`;
}

/** Lê o `volta` de um caso: de que aba e com quais filtros ele foi aberto. */
export function lerVolta(volta: string | null): { aba: AbaDaFila; filtros: Filtros } | null {
  if (!volta?.startsWith('#/')) return null;
  const [rota, q] = volta.slice(2).split('?');
  const aba = (Object.keys(ROTA_DA_ABA) as AbaDaFila[]).find((a) => ROTA_DA_ABA[a] === rota);
  if (!aba) return null;
  return { aba, filtros: deParametros(new URLSearchParams(q ?? '')) };
}

/** Endereço de um caso que leva o contexto junto. */
export const enderecoDoCaso = (id: string, volta: string | null) =>
  `#/caso?id=${id}${volta ? `&volta=${encodeURIComponent(volta)}` : ''}`;

/** O `volta` do endereço atual (o caso não recebe os parâmetros por prop). */
export function voltaAtual(): string | null {
  const h = window.location.hash;
  const i = h.indexOf('?');
  if (i < 0) return null;
  return new URLSearchParams(h.slice(i + 1)).get('volta');
}

/* ---------- rolagem: voltar exatamente onde estava ---------- */

const CHAVE = (endereco: string) => `dv-rolagem:${endereco}`;
export function guardarRolagem(endereco: string) {
  try {
    sessionStorage.setItem(CHAVE(endereco), String(Math.round(window.scrollY)));
  } catch {
    /* sem armazenamento: só não volta para a posição */
  }
}
export function retomarRolagem(endereco: string): number | null {
  try {
    const v = sessionStorage.getItem(CHAVE(endereco));
    sessionStorage.removeItem(CHAVE(endereco));
    return v === null ? null : Number(v);
  } catch {
    return null;
  }
}

/** "Genérico · Vitória · Nova" — o que a fila lateral mostra no topo. */
export function resumoDoContexto(
  f: Filtros,
  nomes: { setor?: string | undefined; pessoa?: string | undefined },
): { titulo: string; periodo: string; chips: string[] } {
  const { de, ate } = intervalo(f);
  const periodo =
    f.periodo === 'tudo'
      ? 'todo o período'
      : de === ate
        ? diaBR(de)
        : `${diaBR(de).slice(0, 5)} → ${diaBR(ate).slice(0, 5)}`;
  const chips: string[] = [];
  if (f.etapa) chips.push(ROTULO_DA_ETAPA[f.etapa]);
  if (f.pessoa) chips.push(nomes.pessoa ?? 'Balconista');
  for (const e of f.estados) chips.push(ROTULO_DO_ESTADO[e]);
  for (const r of f.resultados) chips.push(ROTULO_DO_RESULTADO[r]);
  for (const t of f.etiquetas) chips.push(ROTULO_DA_ETIQUETA[t]);
  if (f.faixa) chips.push(`dif. ${FAIXAS.find((x) => x.faixa === f.faixa)?.rotulo ?? ''}`);
  if (f.busca?.trim()) chips.push(`“${f.busca.trim()}”`);
  return { titulo: f.setor ? (nomes.setor ?? 'Categoria') : 'Fila', periodo, chips };
}
